import { OnInit, Component } from '@angular/core';
import { SelectItem, LazyLoadEvent } from 'primeng';
import { SupplierService } from 'src/app/services/supplier.service';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { LoggerService } from '../../services/logger.service';
import { TaggedLogger } from '../../services/taggedLogger';
import moment from 'moment';

@Component({
  selector: 'supplier-deactivation-admin',
  templateUrl: './supplier-deactivation-admin.component.html',
  styleUrls: ['./supplier-deactivation-admin.component.css'],
})
export class SupplierDeactivationAdminComponent implements OnInit {
  constructor(
    private supplierService: SupplierService,
    private formBuilder: UntypedFormBuilder,
    private loggerService: LoggerService,
  ) {}

  private logger: TaggedLogger;
  deactivationSettings: any;
  selectedSetting: any;
  form: UntypedFormGroup;
  ctrls: { [p: string]: AbstractControl };

  suppliersData: any[] = [];
  viewData: any[] = [];

  showSettingDialog = false;

  // one for each datatable
  first: number[] = [];
  numberOfRows: number[] = [];
  sortField: string[] = [];
  sortOrder: number[] = [];

  async ngOnInit() {
    this.logger = this.loggerService.taggedLogger('Supplier Deactivation Admin');

    this.form = this.formBuilder.group({
      deactivation_DaysSinceLastJob: new UntypedFormControl('', [
        Validators.required,
        Validators.min(1),
        Validators.max(10000),
      ]),
      deactivation_PreWarn_DaysSinceLastJob: new UntypedFormControl('', [Validators.required]),
      deactivation_RegisteredDays: new UntypedFormControl('', [
        Validators.required,
        Validators.min(1),
        Validators.max(365),
      ]),
    });

    this.ctrls = this.form.controls;

    this.loadDeactivationSettings();

    // load all suppliers who were deactivated by the system
    await this.supplierService.getDeactivatedSuppliers().then((result) => {
      this.suppliersData.push(result);

      this.logger.trace('Got deactivated suppliers', result);
      console.log('Got deactivated suppliers', result);
    });

    // load all suppliers who have been prevented from deactivation by the system
    await this.supplierService.getPreventedSuppliers().then((result) => {
      this.suppliersData.push(result);

      this.logger.trace('Got "prevented" suppliers', result);
      console.log('Got "prevented" suppliers', result);
    });

    // load all suppliers who have been pre-warned by the system
    await this.supplierService.getPreWarnedSuppliers().then((result) => {
      this.suppliersData.push(result);

      this.logger.trace('Got pre-warned suppliers', result);
      console.log('Got pre-warned suppliers', result);
    });

    this.first.push(0, 0, 0);
    this.numberOfRows.push(20, 20, 20);
    this.sortField.push('', '', '');
    this.sortOrder.push(0, 0, 0);

    for (let i = 0; i < 3; i++) {
      this.viewData[i] = this.getPageData(i);
    }
  }

  loadDeactivationSettings() {
    // load deactivation settings (organisation sub-types relevant to suppliers)
    this.supplierService.getDeactivationSettings().then((result) => {
      this.deactivationSettings = result;

      this.logger.trace('Got deactivation settings', this.deactivationSettings);
    });
  }

  onGridLazyLoad(event: LazyLoadEvent, index: number) {
    this.first[index] = event.first;
    this.numberOfRows[index] = event.rows;
    this.sortField[index] = event.sortField;
    this.sortOrder[index] = event.sortOrder;

    console.log('Lazy load event: ', event);

    if (this.suppliersData[index]) {
      this.viewData[index] = this.getPageData(index);
    }
  }

  getPageData(index: number) {
    const start = this.first[index];
    const rows = this.numberOfRows[index];
    const sortField = this.sortField[index];
    const sortOrder = this.sortOrder[index];

    console.log('sort field: ', sortField);

    let array = this.suppliersData[index].sort((a, b) => {
      switch (sortField) {
        case 'name':
          return this.stringSort(a.name, b.name, sortOrder);
        case 'expiryDate':
          return this.dateSort(a.expiryDate, b.expiryDate, sortOrder);
        case 'actionDateTime':
          return this.dateSort(a.actionDateTime, b.actionDateTime, sortOrder);
        case 'deactivatedDate':
          return this.dateSort(a.deactivatedDate, b.deactivatedDate, sortOrder);
        case 'reason':
          return this.stringSort(a.inactiveReason, b.inactiveReason, sortOrder);
        case 'category':
          return this.stringSort(
            a.deactivationReason.reasonDescription,
            b.deactivationReason.reasonDescription,
            sortOrder,
          );
        default:
          return this.stringSort(a, b, sortOrder);
      }
    });

    let val = array.slice(start, start + rows);

    return val;
  }

  stringSort(a, b, order): number {
    let str1 = !a ? '' : a.toString();
    let str2 = !b ? '' : b.toString();

    if (order == 1) return str1.localeCompare(str2);
    else return str2.localeCompare(str1);
  }

  dateSort(a, b, order): number {
    if (order == 1)
      return (
        moment(a != null ? a : moment.max()).unix() - moment(b != null ? b : moment.max()).unix()
      );
    else
      return (
        moment(b != null ? b : moment.min()).unix() - moment(a != null ? a : moment.min()).unix()
      );
  }

  numberSort(a, b, order): number {
    return order == 1 ? a - b : b - a;
  }

  editSetting(item) {
    this.form.patchValue({
      deactivation_DaysSinceLastJob: item.deactivation_DaysSinceLastJob,
      deactivation_PreWarn_DaysSinceLastJob: item.deactivation_PreWarn_DaysSinceLastJob,
      deactivation_RegisteredDays: item.deactivation_RegisteredDays,
    });

    this.selectedSetting = item;
    this.showSettingDialog = true;
  }

  saveSetting(setting) {
    if (this.form.invalid) {
      return;
    }

    setting = { ...setting, ...this.form.value };

    this.showSettingDialog = false;

    this.supplierService.saveDeactivationSettings(setting).then(() => {
      this.loadDeactivationSettings();
    });
  }

  downloadToCSV(index: number) {
    switch (index) {
      case 0:
        console.log('Deactivation CSV');
        this.supplierService.getDeactivationCSV();
        break;
      case 1:
        console.log('Prevented CSV');
        this.supplierService.getPreventedCSV();
        break;
      case 2:
        console.log('PreWarned CSV');
        this.supplierService.getPreWarnedCSV();
        break;
    }
  }
}
